import {
  ProductHighlight,
  ProductHighlightDetail,
  ProductHighlightHead,
} from '@diamond/shared/types';
import { useQuery } from '@tanstack/react-query';

import { mapApiProductsToUI } from '../adapters/highlightAdapter';
import {
  getOneProductHighlight,
  getProductHighlightsHeads,
  getProductHighlightsLegacy,
  getProductsFromHighlight,
} from '../queries/products';
import { useAuthStore } from '../stores';
import { useBuyerHomeQueryGate } from './useBuyerHomeQueryGate';

export const useGetHomepageHighlights = (options?: { enabled?: boolean }) => {
  const { canQuery, queryToken, selectedShipToId } = useBuyerHomeQueryGate();
  const isNew = options?.enabled ?? true;

  const newQuery = useQuery<ProductHighlight>({
    queryKey: ['HomepageHighlights', selectedShipToId],
    enabled: canQuery && isNew,
    staleTime: 1000 * 60 * 4,
    refetchOnWindowFocus: false,
    notifyOnChangeProps: [
      'data',
      'error',
      'status',
      'isFetched',
      'isPlaceholderData',
    ],
    queryFn: async () => {
      const heads = await getProductHighlightsHeads(queryToken, selectedShipToId);

      if (!heads || heads.length === 0) return [] as ProductHighlight;

      const size = 20;
      const page = 1;
      const itemsByHead = await Promise.all(
        heads.map((h: ProductHighlightHead) =>
          getProductsFromHighlight(queryToken, h.id, {
            size,
            page,
            warehouse: selectedShipToId,
          })
        )
      );

      return heads.map((h, idx) => ({
        id: h.id,
        title: h.title,
        index: h.index ?? idx,
        distribution_channel: h.distribution_channel ?? [],
        business_segmentation: h.business_segmentation ?? [],
        product_highlight_type: h.product_highlight_type,
        products: mapApiProductsToUI(itemsByHead[idx]?.data ?? []),
      }));
    },
  });

  const legacyQuery = useQuery<ProductHighlight>({
    queryKey: ['HomepageHighlightsLegacy', selectedShipToId],
    enabled: canQuery && !isNew,
    staleTime: 1000 * 60 * 4,
    refetchOnWindowFocus: false,
    notifyOnChangeProps: [
      'data',
      'error',
      'status',
      'isFetched',
      'isPlaceholderData',
    ],
    queryFn: async () => {
      const response = await getProductHighlightsLegacy(
        queryToken,
        selectedShipToId
      );
      return (response ?? []).map((h, idx) => ({
        id: h.id,
        title: h.title,
        index: h.index ?? idx,
        distribution_channel: h.distribution_channel ?? [],
        business_segmentation: h.business_segmentation ?? [],
        product_highlight_type: h.product_highlight_type,
        products: mapApiProductsToUI(h.products ?? []),
      })) as ProductHighlight;
    },
  });

  return isNew ? newQuery : legacyQuery;
};

export const useGetOneProductHighlight = (
  id: string,
  warehouse: string
) => {
  const session = useAuthStore();

  return useQuery<ProductHighlightDetail>({
    queryKey: ['OneProductHighlight', id, warehouse],
    queryFn: () => getOneProductHighlight(session.access_token, id, warehouse),
    enabled: !!id && !!warehouse && !!session.access_token,
    staleTime: 1000 * 60 * 4,
    refetchOnWindowFocus: false,
  });
};
